/**
 * @fileoverview Input/output schemas for personality-matcher-v2.
 *
 * This agent generates a synthetic personality profile from character attributes,
 * which is then used for deterministic matching against the inspiration database.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import {
  SHAPE_DEFINITIONS,
  ATHLETICISM_DEFINITIONS,
  SKINTONE_DEFINITIONS,
  CHEST_DEFINITIONS,
  WAIST_DEFINITIONS,
  HIPS_DEFINITIONS,
  EYE_COLOR_OPTIONS,
  HAIR_COLOR_OPTIONS,
  CHARACTER_ARCHETYPE_NAMES,
  ETHNICITY_L1_NAMES,
} from '@/domains/characters/constants';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Character context extracted from the character's starting snapshot.
 */
export const CharacterContextSchema = z.object({
  name: z.string().describe("The character's name."),
  age: z.string().optional().describe("The character's age (e.g., '28' or 'early 30s')."),
  gender: z.string().optional().describe("The character's gender."),
  height: z.string().optional().describe("The character's height (e.g., '180cm' or 'tall')."),
  personality: z.string().optional().describe("Free-text description of the character's personality and demeanor."),
  physicalDescription: z.string().optional().describe("Free-text description of the character's physical appearance."),
  plotRole: z.string().optional().describe("The character's role in the story and their core identity."),
});

export type CharacterContext = z.infer<typeof CharacterContextSchema>;

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  characterContext: CharacterContextSchema,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Synthetic profile generated by the agent.
 * Uses constrained enum values that match the inspiration database schema.
 */
export const SyntheticProfileSchema = z.object({
  // Numeric attributes (agent must infer/parse from character context)
  age: z.number().min(1).max(120).describe("Estimated age as a number."),
  height: z.number().min(100).max(250).describe("Estimated height in centimeters."),

  // Gender (must match database enum)
  gender: z.enum(['Female', 'Male', 'Non-binary']).describe("Gender identity."),

  // Body attributes (must use exact enum keys)
  shape: z.enum(Object.keys(SHAPE_DEFINITIONS) as [string, ...string[]])
    .describe("Body shape/build type."),
  athleticism: z.enum(Object.keys(ATHLETICISM_DEFINITIONS) as [string, ...string[]])
    .describe("Level of physical fitness/athleticism."),
  chest: z.enum(Object.keys(CHEST_DEFINITIONS) as [string, ...string[]])
    .describe("Chest/upper torso width relative to body type."),
  waist: z.enum(Object.keys(WAIST_DEFINITIONS) as [string, ...string[]])
    .describe("Waist/midsection width relative to body type."),
  hips: z.enum(Object.keys(HIPS_DEFINITIONS) as [string, ...string[]])
    .describe("Hip/lower torso width relative to body type."),
  skinTone: z.enum(Object.keys(SKINTONE_DEFINITIONS) as [string, ...string[]])
    .describe("Skin tone category."),

  // Appearance attributes
  hairColor: z.enum(HAIR_COLOR_OPTIONS as unknown as [string, ...string[]])
    .describe("Hair color."),
  eyeColor: z.enum(EYE_COLOR_OPTIONS as unknown as [string, ...string[]])
    .describe("Eye color."),

  // Archetypes (1-3 that fit the character)
  characterArchetype: z.array(z.enum(CHARACTER_ARCHETYPE_NAMES as unknown as [string, ...string[]])).min(1).max(3)
    .describe("The 1-3 character archetypes that best fit this character."),

  // Ethnicities (record: L1 group -> L2 sub-ethnicities)
  ethnicities: z.record(z.enum(ETHNICITY_L1_NAMES), z.array(z.string()))
    .describe("Likely ethnic backgrounds as a record of L1 group to L2 sub-ethnicities. Keys MUST be from the official L1 list (e.g., { 'Caucasian': ['Gallo-Romance (French, Walloon/Belgian)'], 'East Asian': ['Yamato / Japanese'] })."),

  // Description arrays (agent extracts key traits)
  personalityTraits: z.array(z.string()).min(1).max(5)
    .describe("Key personality traits (1-5 words each)."),
  bodyTraits: z.array(z.string()).min(1).max(5)
    .describe("Key body/physique descriptors (1-5 words each)."),
  faceTraits: z.array(z.string()).min(1).max(5)
    .describe("Key facial feature descriptors (1-5 words each)."),
});

export type SyntheticProfile = z.infer<typeof SyntheticProfileSchema>;

export const OutputSchema = z.object({
  syntheticProfile: SyntheticProfileSchema
    .describe("A synthetic personality profile derived from the character attributes."),
  reasoning: z.string()
    .describe("Brief explanation of how the profile was derived from the character context."),
});

export type Output = z.infer<typeof OutputSchema>;
